import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { generateGuid } from 'utils/guid';
import { z } from 'zod';
import type { AimApplicationDraft } from '../../../model/aimDrafts';
import { nullableTextInput } from './aimFormHelpers';

export const aimApplicationFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  environment: nullableTextInput,
});

type AimApplicationFormInputValues = {
  name: string;
  environment: string;
};

type AimApplicationFormOutputValues = z.output<typeof aimApplicationFormSchema>;

export const toAimApplicationFormValues = (
  row?: AimApplicationDraft,
): AimApplicationFormInputValues => ({
  name: row?.name ?? '',
  environment: row?.environment ?? '',
});

type Props = {
  formType: 'add' | 'edit';
  initialValues?: AimApplicationDraft;
  onClose: () => void;
  onSubmit: (entity: AimApplicationDraft) => void;
};

export const AimApplicationDialog = ({
  formType,
  initialValues,
  onClose,
  onSubmit,
}: Props) => {
  const form = useCustomForm<
    AimApplicationFormInputValues,
    unknown,
    AimApplicationFormOutputValues
  >({
    defaultValues: toAimApplicationFormValues(initialValues),
    resolver: zodResolver(aimApplicationFormSchema),
  });

  const handleSave = form.handleSubmit((values) => {
    const entity: AimApplicationDraft = {
      id:
        formType === 'edit' && initialValues
          ? initialValues.id
          : generateGuid(),
      name: values.name,
      environment: values.environment,
    };
    onSubmit(entity);
    onClose();
  });

  return (
    <SidePanel
      title={
        formType === 'add'
          ? 'Add manual application'
          : 'Edit manual application'
      }
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            variant='primary'
            type='submit'
            icon='fa-floppy-disk'
            disabled={!form.formState.isDirty}
            loading={form.formState.isSubmitting}
            onClick={handleSave}
            dataTestId='aim-application-save'
          >
            {formType === 'add' ? 'Add manual application' : 'Save changes'}
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form>
        <Card className='flex flex-col gap p-sm'>
          <FormTextField
            control={form.control}
            name='name'
            label='Name'
            type='text'
          />
          <FormTextField
            control={form.control}
            name='environment'
            label='Environment (optional)'
            type='text'
          />
        </Card>
      </form>
    </SidePanel>
  );
};
